/*  UI  */
import { Box } from '@auviknetworks/iceberg-ui';
import { Typography } from '@mui/material';

/*  Hooks  */
import { useWindowSize } from 'hooks/windowSize';

/*  React  */
import React, { useEffect, useRef, useState } from 'react';
// @ts-expect-error It is asking me to add the @types/styled-components, but that triggered many errors that should not be there.
import styled from 'styled-components';

/*  Shared  */
import { DarkTooltip, TooltipPlacement } from './Tooltips';

interface TruncatedTextProps {
    className?: string;
    color?: string;
    component?: React.ElementType;
    containerClassName?: string;
    maxLines?: number;
    showTooltip?: boolean;
    sx?: object;
    text: string | React.ReactNode;
    tooltipPlacement?: TooltipPlacement;
    variant?:
        | 'body1'
        | 'body2'
        | 'caption'
        | 'h1'
        | 'h2'
        | 'h3'
        | 'h4'
        | 'h5'
        | 'h6';
}

interface ContainerProps {
    maxLines?: number;
}

const TruncatedContainer = styled(Box)<ContainerProps>(
    ({ maxLines }: { maxLines: number }) => ({
        display: '-webkit-box',
        hyphens: 'auto',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: maxLines,
        wordBreak: 'break-word'
    })
);

/**
 * A component that truncates text after a specified number of lines and shows a tooltip on hover.
 *
 * @component
 * @param {object} props - The component props
 * @param {string} props.text - The text to display and potentially truncate
 * @param {number} [props.maxLines=2] - Maximum number of lines to show before truncating
 * @param {TooltipPlacement} [props.tooltipPlacement='top'] - Placement of the tooltip
 * @param {boolean} [props.showTooltip=true] - Whether to show tooltip on hover
 * @param {string} [props.variant='body2'] - Typography variant for the text
 * @param {string} [props.color] - Custom color for the text
 * @param {object} [props.sx] - Additional MUI sx props
 * @param {string} [props.className] - Additional CSS class name
 * @returns {JSX.Element} A truncated text component with optional tooltip
 *
 * @example
 * // Basic usage with 2 lines
 * <TruncatedText text="This is a very long text that will be truncated after two lines" />
 *
 * @example
 * // Custom number of lines with tooltip placement
 * <TruncatedText
 *   text="Very long text here"
 *   maxLines={3}
 *   tooltipPlacement={TooltipPlacement.BOTTOM}
 * />
 *
 * @example
 * // Without tooltip
 * <TruncatedText
 *   text="Long text"
 *   maxLines={1}
 *   showTooltip={false}
 * />
 */
export const TruncatedText = ({
    className,
    color,
    component = 'span',
    containerClassName,
    maxLines = 1,
    showTooltip = true,
    sx = {},
    text,
    tooltipPlacement = TooltipPlacement.TOP,
    variant = 'body2'
}: TruncatedTextProps) => {
    const [isTruncated, setIsTruncated] = useState(false);
    const textRef = useRef<HTMLDivElement>(null);
    const { windowSize } = useWindowSize();

    // Check if text is actually truncated
    useEffect(() => {
        if (textRef.current) {
            const element = textRef.current;
            const isTextTruncated = element.scrollHeight > element.clientHeight;
            setIsTruncated(isTextTruncated);
        }
    }, [text, maxLines, windowSize]);

    // Only show tooltip if text is actually truncated and tooltip is enabled
    const shouldShowTooltip = showTooltip && isTruncated;

    const textContent = (
        <TruncatedContainer
            key={isTruncated ? 'truncated' : 'not-truncated'}
            ref={textRef}
            maxLines={maxLines}
            className={className}
            sx={sx}
        >
            <Typography
                variant={variant}
                color={color}
                component={component}
                sx={{ whiteSpace: 'pre-line', ...sx }}
                className={containerClassName}
            >
                {text}
            </Typography>
        </TruncatedContainer>
    );

    if (!shouldShowTooltip) {
        return textContent;
    }

    return (
        <Box sx={{ alignItems: 'center', display: 'flex' }}>
            <DarkTooltip
                title={text}
                placement={tooltipPlacement}
                arrow
                sx={{ whiteSpace: 'pre-line' }}
            >
                {textContent}
            </DarkTooltip>
        </Box>
    );
};
